import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

export interface Subdomain {
  id?: string;
  userId: string;
  subdomain: string; // e.g., "scaledots"
  customDomain?: string; // e.g., "scaledots.com" (optional)
  isActive: boolean;
  isVerified: boolean;
  verificationToken?: string;
  dnsRecords?: {
    cname?: string;
    txt?: string;
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastVerifiedAt?: Timestamp;
}

export interface LandingPage {
  id?: string;
  userId: string;
  title: string;
  slug: string; // e.g., "xyz" for scaledots.sm.page/xyz
  subdomainId?: string; // Reference to subdomain
  content: {
    html: string;
    css?: string;
    js?: string;
  };
  settings: {
    seoTitle?: string;
    seoDescription?: string;
    favicon?: string;
    customCss?: string;
    analyticsId?: string;
  };
  formFields: Array<{
    id: string;
    type: 'email' | 'firstName' | 'lastName' | 'custom';
    label: string;
    required: boolean;
    hidden: boolean;
    placeholder?: string;
  }>;
  styling: {
    primaryColor: string;
    secondaryColor: string;
    backgroundColor: string;
    textColor: string;
    fontFamily: string;
    borderRadius: string;
  };
  isPublished: boolean;
  isPublic: boolean; // For subdomain access
  publishedAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  viewCount: number;
  conversionCount: number;
}

export interface SubdomainAnalytics {
  subdomainId: string;
  date: string; // YYYY-MM-DD
  pageViews: number;
  uniqueVisitors: number;
  conversions: number;
  topPages: Array<{
    slug: string;
    views: number;
  }>;
}

class SubdomainService {
  // Subdomain Management
  async createSubdomain(userId: string, subdomain: string, customDomain?: string): Promise<string> {
    // Validate subdomain format
    if (!this.isValidSubdomain(subdomain)) {
      throw new Error('Invalid subdomain format. Use only letters, numbers, and hyphens.');
    }

    // Check if subdomain is available
    const isAvailable = await this.isSubdomainAvailable(subdomain);
    if (!isAvailable) {
      throw new Error('Subdomain is already taken.');
    }

    // Check if custom domain is available (if provided)
    if (customDomain && customDomain.trim()) {
      const q = query(
        collection(db, 'subdomains'),
        where('customDomain', '==', customDomain.toLowerCase().trim())
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        throw new Error(`Custom domain ${customDomain} is already in use by another workspace.`);
      }
    }

    const subdomainData: Omit<Subdomain, 'id'> = {
      userId,
      subdomain: subdomain.toLowerCase(),
      isActive: true, // Make subdomains active immediately
      isVerified: true, // Skip verification for now
      verificationToken: this.generateVerificationToken(),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    // Only add customDomain if it's provided and not empty
    if (customDomain && customDomain.trim()) {
      subdomainData.customDomain = customDomain.toLowerCase().trim();
    }

    const docRef = await addDoc(collection(db, 'subdomains'), subdomainData);
    return docRef.id;
  }

  async getSubdomain(subdomainId: string): Promise<Subdomain | null> {
    const docRef = doc(db, 'subdomains', subdomainId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Subdomain;
    }
    return null;
  }

  async getSubdomainBySubdomain(subdomain: string): Promise<Subdomain | null> {
    const q = query(
      collection(db, 'subdomains'),
      where('subdomain', '==', subdomain.toLowerCase())
    );
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() } as Subdomain;
    }
    return null;
  }

  async getUserSubdomains(userId: string): Promise<Subdomain[]> {
    try {
      const q = query(
        collection(db, 'subdomains'),
        where('userId', '==', userId)
      );
      const querySnapshot = await getDocs(q);
      
      const subdomains = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Subdomain[];
      
      // Sort by createdAt in descending order
      return subdomains.sort((a, b) => {
        const aTime = a.createdAt?.toMillis() || 0;
        const bTime = b.createdAt?.toMillis() || 0;
        return bTime - aTime;
      });
    } catch (error) {
      console.error('Error fetching user subdomains:', error);
      throw error;
    }
  }

  async updateSubdomain(subdomainId: string, updates: Partial<Subdomain>): Promise<void> {
    const docRef = doc(db, 'subdomains', subdomainId);
    
    // Filter out undefined values to avoid Firestore errors
    const cleanUpdates = Object.fromEntries(
      Object.entries(updates).filter(([_, value]) => value !== undefined)
    );
    
    await updateDoc(docRef, {
      ...cleanUpdates,
      updatedAt: Timestamp.now()
    });
  }

  // Method to activate existing subdomains (for migration)
  async activateSubdomain(subdomainId: string): Promise<void> {
    await this.updateSubdomain(subdomainId, {
      isActive: true,
      isVerified: true
    });
  }

  async deleteSubdomain(subdomainId: string): Promise<void> {
    // First, unpublish all landing pages using this subdomain
    const landingPages = await this.getLandingPagesBySubdomain(subdomainId);
    for (const page of landingPages) {
      await this.updateLandingPage(page.id!, { isPublic: false });
    }

    // Then delete the subdomain
    const docRef = doc(db, 'subdomains', subdomainId);
    await deleteDoc(docRef);
  }

  // Landing Page Management
  async createLandingPage(userId: string, subdomainId: string, data: Partial<LandingPage>): Promise<string> {
    const landingPageData: Omit<LandingPage, 'id'> = {
      userId,
      subdomainId,
      title: data.title || 'Untitled Landing Page',
      slug: data.slug || this.generateSlug(data.title || 'untitled'),
      content: data.content || { html: '', css: '', js: '' },
      settings: data.settings || {},
      formFields: data.formFields || [],
      styling: data.styling || {
        primaryColor: '#3B82F6',
        secondaryColor: '#1E40AF',
        backgroundColor: '#FFFFFF',
        textColor: '#1F2937',
        fontFamily: 'Inter',
        borderRadius: '8px'
      },
      isPublished: false,
      isPublic: false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      viewCount: 0,
      conversionCount: 0
    };

    const docRef = await addDoc(collection(db, 'landing_pages'), landingPageData);
    return docRef.id;
  }

  async getLandingPage(pageId: string): Promise<LandingPage | null> {
    const docRef = doc(db, 'landing_pages', pageId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as LandingPage;
    }
    return null;
  }

  async getLandingPageBySlug(subdomain: string, slug: string): Promise<LandingPage | null> {
    // First get the subdomain
    const subdomainData = await this.getSubdomainBySubdomain(subdomain);
    if (!subdomainData || !subdomainData.isActive) {
      return null;
    }

    // Then get the landing page
    const q = query(
      collection(db, 'landing_pages'),
      where('subdomainId', '==', subdomainData.id),
      where('slug', '==', slug),
      where('isPublic', '==', true)
    );
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() } as LandingPage;
    }
    return null;
  }

  async getLandingPagesBySubdomain(subdomainId: string): Promise<LandingPage[]> {
    const q = query(
      collection(db, 'landing_pages'),
      where('subdomainId', '==', subdomainId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as LandingPage[];
  }

  async updateLandingPage(pageId: string, updates: Partial<LandingPage>): Promise<void> {
    const docRef = doc(db, 'landing_pages', pageId);
    
    // Filter out undefined values to avoid Firestore errors
    const cleanUpdates = Object.fromEntries(
      Object.entries(updates).filter(([_, value]) => value !== undefined)
    );
    
    await updateDoc(docRef, {
      ...cleanUpdates,
      updatedAt: Timestamp.now()
    });
  }

  async publishLandingPage(pageId: string): Promise<void> {
    await this.updateLandingPage(pageId, {
      isPublished: true,
      isPublic: true,
      publishedAt: Timestamp.now()
    });
  }

  async unpublishLandingPage(pageId: string): Promise<void> {
    await this.updateLandingPage(pageId, {
      isPublished: false,
      isPublic: false
    });
  }

  async deleteLandingPage(pageId: string): Promise<void> {
    const docRef = doc(db, 'landing_pages', pageId);
    await deleteDoc(docRef);
  }

  // Analytics
  async recordPageView(subdomain: string, slug: string): Promise<void> {
    const subdomainData = await this.getSubdomainBySubdomain(subdomain);
    if (!subdomainData) return;

    const landingPage = await this.getLandingPageBySlug(subdomain, slug);
    if (!landingPage) return;

    // Update view count
    await this.updateLandingPage(landingPage.id!, {
      viewCount: landingPage.viewCount + 1
    });

    // Record analytics (you might want to use a separate analytics service)
    await this.recordAnalytics(subdomainData.id!, slug, 'page_view');
  }

  async recordConversion(subdomain: string, slug: string): Promise<void> {
    const subdomainData = await this.getSubdomainBySubdomain(subdomain);
    if (!subdomainData) return;

    const landingPage = await this.getLandingPageBySlug(subdomain, slug);
    if (!landingPage) return;

    // Update conversion count
    await this.updateLandingPage(landingPage.id!, {
      conversionCount: landingPage.conversionCount + 1
    });

    // Record analytics
    await this.recordAnalytics(subdomainData.id!, slug, 'conversion');
  }

  // Utility Methods
  private isValidSubdomain(subdomain: string): boolean {
    const regex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
    return regex.test(subdomain) && subdomain.length >= 3 && subdomain.length <= 63;
  }

  private async isSubdomainAvailable(subdomain: string): Promise<boolean> {
    const existing = await this.getSubdomainBySubdomain(subdomain);
    return !existing;
  }

  private generateVerificationToken(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  private generateSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  private async recordAnalytics(subdomainId: string, slug: string, event: string): Promise<void> {
    // This would integrate with your analytics system
    // For now, we'll just log it
    console.log(`Analytics: ${event} for ${subdomainId}/${slug}`);
  }

  // DNS Verification
  async verifyDNS(subdomainId: string): Promise<{ success: boolean; message: string }> {
    const subdomain = await this.getSubdomain(subdomainId);
    if (!subdomain) {
      return { success: false, message: 'Subdomain not found' };
    }

    // This would implement actual DNS verification
    // For now, we'll simulate it
    const isVerified = Math.random() > 0.5; // Simulate 50% success rate
    
    if (isVerified) {
      await this.updateSubdomain(subdomainId, {
        isVerified: true,
        isActive: true,
        lastVerifiedAt: Timestamp.now()
      });
      return { success: true, message: 'DNS verification successful' };
    } else {
      return { success: false, message: 'DNS verification failed. Please check your DNS settings.' };
    }
  }

  // Get DNS instructions for subdomain setup
  getDNSInstructions(subdomain: string, customDomain?: string): {
    cname: string;
    txt: string;
    instructions: string[];
  } {
    const cname = `${subdomain}.sm.page`;
    const txt = `scubamail-verification=${this.generateVerificationToken()}`;
    
    const instructions = customDomain 
      ? [
          `Add a CNAME record: ${subdomain} -> ${cname}`,
          `Add a TXT record: ${subdomain} -> ${txt}`,
          'Wait 5-10 minutes for DNS propagation',
          'Click "Verify DNS" in your dashboard'
        ]
      : [
          `Your subdomain will be available at: https://${subdomain}.sm.page`,
          'No DNS configuration needed for .sm.page subdomains',
          'Subdomain will be active immediately after creation'
        ];

    return { cname, txt, instructions };
  }
}

export const subdomainService = new SubdomainService();
